import { useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase.ts";
import { emptyAnswer, firstSession, withDeadline } from "./lib/requests.ts";
import Login from "./screens/Login.tsx";
import Venues, { type Venue } from "./screens/Venues.tsx";
import Tracker, { SummaryTable } from "./screens/Tracker.tsx";
import Stats from "./screens/Stats.tsx";
import Team from "./screens/Team.tsx";
import { loadSession, mutateSession, saveSession, type LocalSession } from "./lib/queue.ts";
import { emptyState, resumeState, type TrackerState } from "./lib/tracker.ts";
import { closeAbandoned, lastKnownAt } from "./lib/abandon.ts";
import { useBackgroundSync } from "./lib/useBackgroundSync.ts";
import { clock, type SessionSummary } from "./lib/summary.ts";

type View =
  | { at: "venues" }
  | { at: "starting"; venue: Venue }
  | { at: "tracking"; session: LocalSession; initial: TrackerState }
  | { at: "done"; endedAt: number; summary: SessionSummary };

// `collector` is the stored value; "Field observer" is what people see. The
// database keeps the old word because more than twenty policies name it.
type Role = "admin" | "collector";
type Tab = "venues" | "stats" | "team";

const ROLE_LABEL: Record<Role, string> = { admin: "Admin", collector: "Field observer" };

// The last role this phone saw for a person. A display hint only, so a weak
// signal at app start does not lock out someone already on the team. The
// database still decides what anyone can read or write.
const roleKey = (userId: string) => `field-observation.role.${userId}`;
function cachedRole(userId: string): Role | undefined {
  try {
    const v = localStorage.getItem(roleKey(userId));
    return v === "admin" || v === "collector" ? v : undefined;
  } catch {
    return undefined;
  }
}
function cacheRole(userId: string, role: Role | null) {
  try {
    if (role) localStorage.setItem(roleKey(userId), role);
    else localStorage.removeItem(roleKey(userId));
  } catch {
    // Storage blocked, as in some private windows. Nothing to remember then.
  }
}

// The owner flag is remembered the same way and for the same reason: without it
// a weak signal at app start left the owner reading as a plain admin, with the
// Team screen quietly offering less than it should and nothing saying why.
const ownerKey = (userId: string) => `field-observation.owner.${userId}`;
function cachedOwner(userId: string): boolean {
  try {
    return localStorage.getItem(ownerKey(userId)) === "1";
  } catch {
    return false;
  }
}
function cacheOwner(userId: string, owner: boolean) {
  try {
    if (owner) localStorage.setItem(ownerKey(userId), "1");
    else localStorage.removeItem(ownerKey(userId));
  } catch {
    // Same as above: nothing to remember.
  }
}

// The person's name, remembered for the same reason as the role: the start
// screen says who is observing, and a weak signal should not blank it.
const nameKey = (userId: string) => `field-observation.name.${userId}`;
function cachedName(userId: string): string | null {
  try {
    return localStorage.getItem(nameKey(userId));
  } catch {
    return null;
  }
}
function cacheName(userId: string, name: string | null) {
  try {
    if (name) localStorage.setItem(nameKey(userId), name);
    else localStorage.removeItem(nameKey(userId));
  } catch {
    // Same as above: nothing to remember.
  }
}

// supabase-js reports a dropped connection in the browser's own wording, which
// differs by browser and means nothing to someone standing in a food court.
function plain(message: string): string {
  return /failed to fetch|networkerror|load failed|network request failed/i.test(message)
    ? "No connection. Move somewhere with signal and try again."
    : message;
}

const when = (ms: number) =>
  new Date(ms).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });

function Brand() {
  return (
    <span className="brand">
      <span className="brand-dot" aria-hidden />
      <span className="brand-name">Field Observation</span>
    </span>
  );
}

export default function App() {
  const [auth, setAuth] = useState<Session | null | undefined>(undefined);
  // Set when the sign-in on this phone could not be renewed at start: someone
  // still signed in, who needs signal for a moment, not the sign-in screen.
  const [authRetry, setAuthRetry] = useState<string | null>(null);
  const [authTry, setAuthTry] = useState(0);
  // undefined while loading, null when the signed-in user has no role.
  const [profile, setProfile] = useState<Role | null | undefined>(undefined);
  // The owner is an admin who can also hand out admin access and take access
  // away. Remembered on this phone alongside the role, for the same reason: it
  // decides what the Team screen offers, and the database refuses both actions
  // to everyone else whatever this flag says.
  const [owner, setOwner] = useState(false);
  // The observer's name, asked once in place of "Which cleaner?". undefined
  // when unknown (a failed read), null when they have not given one yet.
  const [name, setName] = useState<string | null | undefined>(undefined);
  const [nameSkipped, setNameSkipped] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [profileErr, setProfileErr] = useState<string | null>(null);
  const [profileTry, setProfileTry] = useState(0);
  const [view, setView] = useState<View>({ at: "venues" });
  const [tab, setTab] = useState<Tab>("venues");
  const [err, setErr] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const startLock = useRef(false);

  const userId = auth?.user?.id ?? null;
  const [sync, refreshSync] = useBackgroundSync(userId, view.at === "tracking" ? view.session.id : null);

  // The first answer on who is signed in. A renewal that failed for want of
  // signal used to come back as nobody, so someone still signed in was shown
  // the sign-in screen, after "Loading..." for as long as the renewal took
  // (forever, when it stalled). firstSession tells the two apart and stops
  // waiting after 15 s; the renewal carries on and any later session clears it.
  useEffect(() => {
    let live = true;
    setAuthRetry(null);
    void firstSession(supabase.auth).then((r) => {
      if (!live) return;
      if ("retry" in r) setAuthRetry(r.retry);
      else setAuth(r.session);
    });
    return () => { live = false; };
  }, [authTry]);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // INITIAL_SESSION carries null both for nobody signed in and for a
      // sign-in that could not be renewed; the read above decides that one.
      if (event === "INITIAL_SESSION") return;
      if (s) setAuthRetry(null);
      setAuth(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // A new sign-in starts from the venue list. The view used to survive signing
  // out, so signing back in remounted the tracker with empty state and restarted
  // block numbers at 1, and a second person signing in on the same phone landed
  // inside the first person's session. Unfinished sessions come back through
  // the resume offer instead, with their state read from the queue.
  useEffect(() => {
    setView({ at: "venues" });
    setTab("venues");
    setErr(null);
  }, [userId]);

  // The profile row carries the role, and it is written by a trigger on signup,
  // never from here. `profiles` has a self-read policy and no insert or update
  // policy, on purpose: anyone who can write their own `role` can make
  // themselves an admin from the browser console.
  //
  // A failed read is not "no role". Weak signal at app start used to tell an
  // invited observer they were not on the team, with only Sign out to press.
  useEffect(() => {
    setProfile(undefined);
    setOwner(false);
    setName(undefined);
    setNameSkipped(false);
    setEditingName(false);
    setProfileErr(null);
    if (!userId) return;
    let live = true;
    const failed = (message: string) => {
      if (!live) return;
      const known = cachedRole(userId);
      if (known) { setProfile(known); setOwner(cachedOwner(userId)); setName(cachedName(userId) ?? undefined); }
      else setProfileErr(plain(message));
    };
    void (async () => {
      // Cut off at 15 s, the sign-in renewal included. Without a sign-in the
      // read is not sent at all and comes back as an error, never as "no
      // role": it used to go out anonymous, find nothing, and tell a team
      // member they were not on the team, wiping the role this phone had.
      const { data: rows, error } = await withDeadline((signal) => supabase
        .from("profiles").select("role,revoked_at,is_owner,display_name").eq("id", userId)
        .abortSignal(signal));
      if (!live) return;
      if (error) { failed(error.message); return; }
      // Read as a list rather than maybeSingle, which answers null both for no
      // row and for a success with no body. No row is an empty list; anything
      // that is not a list is a failed read, not "not on the team".
      if (!Array.isArray(rows)) { failed(emptyAnswer.message); return; }
      const data = rows[0] ?? null;
      // Revoked is the same as never added: the database shows it nothing.
      const role = data && data.revoked_at === null ? (data.role as Role) : null;
      const isOwner = role !== null && data?.is_owner === true;
      const known = role !== null ? ((data?.display_name as string | null) ?? null) : null;
      cacheRole(userId, role);
      cacheOwner(userId, isOwner);
      cacheName(userId, known);
      setProfile(role);
      setOwner(isOwner);
      setName(known);
    })().catch((e: unknown) => failed(e instanceof Error ? e.message : String(e)));
    return () => { live = false; };
  }, [userId, profileTry]);

  if (auth === undefined) {
    if (!authRetry) return <div className="pad muted">Loading...</div>;
    // No Sign out here: auth-js refuses to sign out while the renewal is
    // failing, so the button would do nothing.
    return (
      <>
        <nav className="top"><Brand /></nav>
        <main className="main"><div className="pad">
          <h1>Could not renew your sign-in</h1>
          <p className="err">{authRetry}</p>
          <p className="muted">You are still signed in on this phone. Anything recorded here stays on it.</p>
          <button className="primary" onClick={() => setAuthTry((n) => n + 1)}>Try again</button>
        </div></main>
      </>
    );
  }
  if (auth === null) return <Login />;

  const unsent = sync.waiting + sync.endsWaiting;
  const signOut = () => {
    const warning = sync.unreadable
      ? "This phone's storage could not be read, so some records may not have uploaded. Sign out anyway?"
      : unsent > 0
        ? `${unsent} ${unsent === 1 ? "record has" : "records have"} not uploaded yet. ` +
          "They stay on this phone and upload the next time you sign in here. Sign out anyway?"
        : null;
    if (warning && !confirm(warning)) return;
    void supabase.auth.signOut();
  };
  const bare = (body: React.ReactNode) => (
    <>
      <nav className="top"><Brand /><span className="top-right"><button onClick={signOut}>Sign out</button></span></nav>
      <main className="main"><div className="pad">{body}</div></main>
    </>
  );

  if (profileErr) {
    return bare(
      <>
        <h1>Could not check your access</h1>
        <p className="err">{profileErr}</p>
        <p className="muted">The first time you open the app on a phone it needs a connection.</p>
        <button className="primary" onClick={() => setProfileTry((n) => n + 1)}>Try again</button>
      </>,
    );
  }
  if (profile === undefined) return <div className="pad muted">Loading...</div>;

  // Invite-only. An address nobody added gets an account but no role, and the
  // database shows it nothing. Say so, rather than show an empty app.
  if (profile === null) {
    return bare(
      <>
        <h1>You're not on the team yet</h1>
        <p>
          You are signed in as <strong>{auth.user.email}</strong>, but this address
          has not been added, or its access was removed, so there is nothing here for it.
        </p>
        <p className="muted">
          Ask an admin to add you as a field observer, then sign in again with
          this same email.
        </p>
      </>,
    );
  }

  const isAdmin = profile === "admin";
  // Asked once: shown until they give a name or skip it for this visit. Never
  // over the tracker, and never when the read failed and the name is unknown.
  const showNameForm = view.at !== "tracking" && (editingName || (name === null && !nameSkipped));

  async function start(venue: Venue) {
    // One tap, one session. A second tap while the first request was still out
    // made a second wave and a second open session for the same cleaner.
    if (startLock.current) return;
    startLock.current = true;
    setStarting(true);
    setErr(null);
    try {
      const now = Date.now();
      // A wave is an explicit deployment. A solo observer gets a wave of one,
      // because a nullable wave silently omits observers from the coverage count.
      const { data: wave, error: we } = await supabase
        .from("observation_waves")
        // Who ran it, which the wave had a column for and never got (DATA-2).
        .insert({ venue_id: venue.id, recorded_by: userId, started_at: now, created_at: now })
        .select("id").single();
      if (we) { setErr(plain(we.message)); return; }

      const id = crypto.randomUUID();
      const { error: se } = await supabase.from("observation_sessions").insert({
        id, venue_id: venue.id, observer_id: userId, chain_root_id: id,
        // No cleaner reference: the pilot owners asked for the observer's name
        // instead, which is on their profile. The column stays for old sessions.
        wave_id: wave.id, cleaner_ref: null, started_at: now, created_at: now,
      });
      if (se) { setErr(plain(se.message)); return; }

      const local: LocalSession = {
        id, venueId: venue.id, waveId: wave.id, cleanerRef: null,
        startedAt: now, endedAt: null,
        pendingBlocks: [], pendingTaps: [], pendingNotes: [], lastSyncAt: null, lastError: null,
        venueName: venue.name, nextSeq: 1, lastSeenAt: now, endSyncedAt: null, observerId: userId!,
      };
      await saveSession(local);
      setView({ at: "tracking", session: local, initial: emptyState() });
    } catch (e) {
      setErr(plain(e instanceof Error ? e.message : String(e)));
    } finally {
      startLock.current = false;
      setStarting(false);
    }
  }

  async function resume(s: LocalSession) {
    const stored = await loadSession(s.id).catch(() => undefined);
    // Only this person's own session, whatever list the offer came from.
    if (!stored || stored.endedAt !== null || stored.observerId !== userId) { refreshSync(); return; }
    setErr(null);
    setView({ at: "tracking", session: stored, initial: resumeState(stored.pendingBlocks, stored.nextSeq) });
  }

  // Closes the open block when the session was last on screen, then lets the
  // background loop send it, end time included.
  async function endUnfinished(s: LocalSession) {
    try {
      await mutateSession(s.id, null, closeAbandoned);
      setErr(null);
    } catch {
      setErr("That session did not end. Try again.");
    }
    refreshSync();
  }

  // The tabs only exist between sessions. Mid-session there is one screen, and
  // nothing to tap that is not a category.
  const showTabs = isAdmin && view.at === "venues" && !showNameForm;

  return (
    <>
      <nav className="top">
        <Brand />
        <span className="top-right">
          <span className={isAdmin ? "role" : "role observer"}>{owner ? "Owner" : ROLE_LABEL[profile]}</span>
          {/* Not mid-session: signing out there used to strand the open block. */}
          {view.at !== "tracking" && <button onClick={signOut}>Sign out</button>}
        </span>
      </nav>

      <main className="main">
      {showTabs && (
        <div className="tabs" role="tablist">
          {(["venues", "stats", "team"] as Tab[]).map((t) => (
            <button key={t} aria-current={tab === t ? "page" : undefined} onClick={() => setTab(t)}>
              {t === "venues" ? "Venues" : t === "stats" ? "Stats" : "Team"}
            </button>
          ))}
        </div>
      )}

      {err && <div className="pad"><p className="err">{err}</p></div>}

      {view.at === "venues" && !showNameForm && (sync.unfinished.length > 0 || unsent > 0) && (
        <div className="pad">
          {sync.unfinished.map((s) => (
            <div key={s.id} className="card resume">
              <h3>Unfinished session</h3>
              <p>
                {s.venueName ?? "A venue"}, started {when(s.startedAt)}
                {s.cleanerRef ? `, ${s.cleanerRef}` : ""}.
              </p>
              <p className="muted">
                It was never ended, usually because the page reloaded or the phone
                slept. Ending it closes it at {when(lastKnownAt(s))}, the last time
                it was on screen.
              </p>
              <button className="primary" onClick={() => void resume(s)}>Resume it</button>
              <button onClick={() => void endUnfinished(s)}>End it</button>
            </div>
          ))}
          {/* The same count the sign-out warning gives. This one used to leave out
              session ends, so it said 2 where sign-out said 3 (UX-8). */}
          {unsent > 0 && (
            <p className="notice">
              {unsent} {unsent === 1 ? "record" : "records"} from earlier sessions
              still to upload. They go up on their own while this app is open with signal.
            </p>
          )}
        </div>
      )}

      {showNameForm && (
        <div className="pad">
          <NameForm
            initial={name ?? null}
            onSaved={(n) => { setName(n); cacheName(auth.user.id, n); setEditingName(false); }}
            onClose={() => { setEditingName(false); setNameSkipped(true); }}
          />
        </div>
      )}

      {view.at === "venues" && !showNameForm && (!isAdmin || tab === "venues") && (
        <Venues canCreate={isAdmin} userId={auth.user.id} onPick={(v) => { setErr(null); setView({ at: "starting", venue: v }); }} />
      )}
      {view.at === "venues" && !showNameForm && isAdmin && tab === "stats" && <Stats />}
      {view.at === "venues" && !showNameForm && isAdmin && tab === "team" && <Team userId={auth.user.id} isOwner={owner} />}

      {view.at === "starting" && !showNameForm && (
        <div className="pad">
          <form className="card" onSubmit={(e) => { e.preventDefault(); void start(view.venue); }}>
            <h2>{view.venue.name}</h2>
            {/* "Which cleaner?" used to be asked here. The pilot owners asked for
                the observer's name instead (2026-09-22), and it also took names
                of cleaners despite its own warning (PRIV-1). */}
            <p className="muted">
              Observing as <strong>{name ?? auth.user.email}</strong>{" "}
              <button type="button" className="link" onClick={() => setEditingName(true)}>Change</button>
            </p>
            <button className="primary" disabled={starting} autoFocus>{starting ? "Starting..." : "Start session"}</button>
            <button type="button" disabled={starting} onClick={() => { setErr(null); setView({ at: "venues" }); }}>Back</button>
          </form>
        </div>
      )}

      {view.at === "tracking" && (
        <Tracker
          key={view.session.id}
          session={view.session}
          initial={view.initial}
          onEnded={(summary) => { setView({ at: "done", endedAt: Date.now(), summary }); refreshSync(); }}
        />
      )}

      {view.at === "done" && (
        <div className="pad">
          <h2>Session ended</h2>
          {/* What was captured, so an observer sees what their taps add up to,
              and a check that it matches what they saw (pilot owners' request). */}
          {view.summary.rows.length > 0 ? (
            <>
              <p>Total time <strong>{clock(view.summary.observedSeconds)}</strong></p>
              <SummaryTable summary={view.summary} />
            </>
          ) : (
            <p className="muted">Nothing was tapped in this session.</p>
          )}
          {/* Only a check made after the session ended may say it is uploaded.
              The done screen used to promise a sync that nothing performed. */}
          {sync.checkedAt < view.endedAt ? (
            <p className="muted">Checking what is left to upload...</p>
          ) : sync.unreadable ? (
            <p className="err">
              This phone's storage could not be read, so it is not known whether
              everything uploaded. Keep the app open; it checks again on its own.
            </p>
          ) : unsent > 0 ? (
            <p className="notice">
              Saved on this phone, not uploaded yet. It goes up on its own while this
              app is open with signal, so keep it open until this says it is done.
            </p>
          ) : (
            <p className="ok">Everything is uploaded.</p>
          )}
          <button className="primary" onClick={() => setView({ at: "venues" })}>Back to venues</button>
        </div>
      )}
      </main>
    </>
  );
}

/** The observer's name, saved to their account through set_my_name: browsers
    hold no write access to profiles, so this is the only way it can change. */
function NameForm({ initial, onSaved, onClose }: {
  initial: string | null;
  onSaved: (name: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    const { data, error } = await supabase.rpc("set_my_name", { p_name: value });
    setSaving(false);
    if (error) {
      setErr(error.code === "22023" ? "A name is 1 to 80 characters." : plain(error.message));
      return;
    }
    // A function returning a row comes back as the row or a one-row array,
    // depending on the client; read the name the database actually stored.
    const row = (Array.isArray(data) ? data[0] : data) as { display_name?: string } | null;
    onSaved(row?.display_name ?? value.trim());
  }

  return (
    <form className="card" onSubmit={save}>
      <h2>{initial ? "Change your name" : "What's your name?"}</h2>
      <p className="muted">So the team knows who observed what.</p>
      <label>Your name
        <input value={value} onChange={(e) => setValue(e.target.value)}
               autoComplete="name" maxLength={80} autoFocus required />
      </label>
      {err && <p className="err">{err}</p>}
      <button className="primary" disabled={saving || value.trim() === ""}>{saving ? "Saving..." : "Save"}</button>
      {/* Never disabled: on a connection that hangs, Skip is the way out. */}
      <button type="button" onClick={onClose}>{initial ? "Cancel" : "Skip for now"}</button>
    </form>
  );
}
